import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import {
  useGetHoldingRealizedGainsSummaryQuery,
  useGetRealizedGainsSummaryQuery,
} from '@/store/holdingsApi';
import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useContext } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { ThemeContext } from '@/contexts/themeContext';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';

import AdvisorGroupSettings from '@/utils/advisorGroupSettings.utility';
import { useGetAdvisorGroupSettingsQuery } from '@/store/settingsApi';
import { CoolBars } from './CoolBars';
import { GainsLossLoadingSkeletons } from './GainsLossLoadingSkeletons';

interface UnrealizedGainLossSummaryProps {
  endDate?: string;
  holdingId?: number;
  accountId?: number;
  investorId?: number;
}

const UnrealizedGainLossSummary = ({
  holdingId,
  accountId,
  investorId,
}: UnrealizedGainLossSummaryProps) => {
  const theme = useTheme();
  const authLoaded = useAuthIsLoaded();
  const { t } = useLocalization();
  const { brandColors } = useContext(ThemeContext);

  const { data: advisorGroupSettings, isLoading: advisorGroupSettingsLoading } =
    useGetAdvisorGroupSettingsQuery(authLoaded ? undefined : skipToken);

  const effectiveExclusions = useEffectiveExclusions();
  const serializedExcludedAccounts =
    effectiveExclusions.excludedAccounts.join(',');
  const serializedExcludedAccountTypes =
    effectiveExclusions.excludedAccountTypes.join(',');
  const serializedExcludedCategories =
    effectiveExclusions.excludedCategories.join(',');

  const shouldUseBrandColors =
    brandColors.hasPrimaryAndSecondary &&
    typeof brandColors.primary === 'string' &&
    typeof brandColors.secondary === 'string';

  const marketValueColor =
    shouldUseBrandColors && brandColors.primary
      ? brandColors.primary
      : '#6FDD98';
  const costBasisColor =
    shouldUseBrandColors && brandColors.secondary
      ? brandColors.secondary
      : 'rgba(148, 186, 212, 0.56)';

  const { data: portfolioData, isLoading: portfolioLoading } =
    useGetRealizedGainsSummaryQuery(
      authLoaded && !holdingId
        ? {
            sort: [{ property: 'PercentChange', direction: 'desc', order: 1 }],
            InvestorId: investorId,
            AccountId: accountId,
            ...(serializedExcludedAccounts
              ? { ExcludedAccounts: serializedExcludedAccounts }
              : {}),
            ...(serializedExcludedAccountTypes
              ? { ExcludedAccountTypes: serializedExcludedAccountTypes }
              : {}),
            ...(serializedExcludedCategories
              ? { ExcludedCategories: serializedExcludedCategories }
              : {}),
          }
        : skipToken,
    );

  const { data: holdingData, isLoading: holdingLoading } =
    useGetHoldingRealizedGainsSummaryQuery(
      authLoaded && holdingId
        ? {
            holdingId,
            sort: [{ property: 'PercentChange', direction: 'desc', order: 1 }],
            startDate: '', // Required for RealizedGainsQueryParams
            endDate: '', // Required for RealizedGainsQueryParams
            investorId,
          }
        : skipToken,
    );

  const data = holdingId ? holdingData : portfolioData;
  const isLoading = holdingId ? holdingLoading : portfolioLoading;
  const isLoadingState =
    !authLoaded || isLoading || advisorGroupSettingsLoading;

  const marketValue = data?.CurrentBalance || 0;
  const costBasis = data?.UnrealizedCostBasis || 0;

  const bars = [
    {
      name: t('marketValue'),
      value: marketValue,
      color: marketValueColor,
    },
    {
      name: t('grid.costBasis'),
      value: costBasis,
      color: costBasisColor,
    },
  ];

  const isDisclaimerVisible =
    AdvisorGroupSettings(advisorGroupSettings).getByKey('ShowCCTMessages');

  if (isLoadingState) {
    return (
      <Box sx={{ mb: 4 }}>
        <GainsLossLoadingSkeletons />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Grid
          container
          spacing={{ xs: 4, md: 8 }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box>
              <Stack
                direction='column'
                flex={1}
                gap={2}
              >
                <Typography
                  fontSize={16}
                  fontWeight={600}
                >
                  {t('summary')}
                </Typography>

                <CoolBars barConfigs={bars} />
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {isDisclaimerVisible && (
        <Typography
          variant='caption'
          fontWeight={400}
          sx={{ color: theme.palette.text.secondary }}
        >
          {t('gainsLosses.unrealizedDisclaimer')}
        </Typography>
      )}
    </Box>
  );
};

export default UnrealizedGainLossSummary;
